import { Injectable, OnDestroy } from '@angular/core';

import {
  concatMap,
  delay,
  distinctUntilChanged,
  filter,
  map,
  merge,
  pairwise,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';

import { AuthToken } from '@shared/signup/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import {
  ServerTimeService,
} from '@tablesready/common-ui';
import { DataLoaderService } from './data-loader.service';
import { AudioService } from '../providers';
import { WaitTimeService } from '@tablesready/frontend-party-observer';
import { PermissionCheckerService } from './permission-checker.service';
import { LocationSwitcherService, LocationSwitcherBadgeService } from '@tablesready/frontend-location-switcher';
import {
  AuthAccountService,
  AuthRestaurantService,
  SocketService,
} from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import {
  AuthService,
  LastLocationIdService,
  TabIdentityService,
  TokenService,
} from '@tablesready/frontend-auth';
import { HeaderTitleService } from '@tablesready/frontend-page-badge';
import { ActivePartiesService } from './active-parties.service';
import { NotificationCenterService } from '@tablesready/frontend-notification-center';
import { isOnFreePlan } from '@shared/user/functions';

@Injectable()
export class AuthenticatedInitializer implements OnDestroy {
  private readonly subscriptions = new Subscription();

  constructor(
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly dataLoaderService: DataLoaderService,
    private readonly audioService: AudioService,
    private readonly waitTimeService: WaitTimeService,
    private readonly socketService: SocketService,
    private readonly lastLocationIdService: LastLocationIdService,
    private readonly alertsService: AlertsService,
    private readonly tokenService: TokenService,
    private readonly locationSwitcherService: LocationSwitcherService,
    private readonly permissionChecker: PermissionCheckerService,
    private readonly accountService: AuthAccountService,
    private readonly authService: AuthService,
    private readonly serverTimeService: ServerTimeService,
    private readonly activePartiesService: ActivePartiesService,
    private readonly headerService: HeaderTitleService,
    private readonly notificationCenterService: NotificationCenterService,
    private readonly locationsSwitcherBadgeService: LocationSwitcherBadgeService,
    private readonly tabIdentityService: TabIdentityService,
  ) {
    const authUserBuildSubscription = this.authRestaurantService.build()
      .pipe(switchMap(() => this.authRestaurantService.listenToChanges()))
      .subscribe();
    this.subscriptions.add(authUserBuildSubscription);

    const locationChanges = this.authRestaurantService.select()
      .pipe(
        pairwise(),
        filter(([prev, current]) => prev.location?._id !== current.location?._id),
        map(([prev, current]): [boolean, string | undefined, string] => ([
          Boolean(!current.location || current.location && current.location._id !== this.lastLocationIdService.get()),
          current.location?._id,
          prev.account?.id,
        ])),
        tap(([switchedExternally, locationId]) => {
          if (switchedExternally && locationId) {
            this.lastLocationIdService.set(locationId);
          }
        }),
        delay(300),
        map(([switchedExternally, , prevAccId]) => ({
          switchedExternally,
          prevAcc: this.accountService.getById(prevAccId),
        })),
        tap(({ switchedExternally, prevAcc }) => {
          if (prevAcc?.enabled && switchedExternally) {
            this.alertsService.addInfo('Current location is not available. Please contact your administrator.');
          } else if (!prevAcc?.enabled && switchedExternally) {
            this.alertsService.addWarning('This organization’s account is unavailable. Please get in touch with the account administrator or support@tablesready.com if you have any questions');
          }
        }),
      )
      .subscribe();

    this.subscriptions.add(locationChanges);

    const tokenChangesSubscription = merge(
      this.socketService.getEventStream<AuthToken | AuthToken[]>(WebSocketEventsEnum.tokenCreated)
        .pipe(
          map(data => (Array.isArray(data) ? data : [data])),
          tap((tokens) => {
            this.tokenService.add(tokens);
            // Non-destructive read: getActive() would DROP an expired-but-recoverable active token
            // and return null, making this look like "no active location" and force-switching a
            // mid-shift user to the newly created location. Only switch when there genuinely is
            // no active location.
            if (!this.tokenService.getActiveLocationId()) {
              this.locationSwitcherService.selectLocation(tokens[0].locationId, true);
            }
          }),
        ),
      this.socketService.getEventStream<string>(WebSocketEventsEnum.tokenRevoked)
        .pipe(tap((locationId) => {
          const ids = Array.isArray(locationId) ? locationId : [locationId];
          this.tokenService.removeByLocationIds(ids);
          this.tabIdentityService.adoptIfLocationRevoked(ids);
        })),
    ).subscribe();
    this.subscriptions.add(tokenChangesSubscription);

    const dataLoadedSubscription = this.dataLoaderService.listenToChanges().subscribe();
    this.subscriptions.add(dataLoadedSubscription);

    const waitlistCountSubscription = this.activePartiesService.waitlistCount()
      .pipe(tap(badgeCount => this.headerService.setDataForRoute('waitlist', {badgeCount})))
      .subscribe();
    this.subscriptions.add(waitlistCountSubscription);

    const reservationCountSubscription = this.activePartiesService.reservationsCount()
      .pipe(tap(badgeCount => this.headerService.setDataForRoute('reservations', {badgeCount})))
      .subscribe();
    this.subscriptions.add(reservationCountSubscription);

    const socketConnectSubscription = this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(take(1))
      .subscribe(() => this.socketService.connect());
    this.subscriptions.add(socketConnectSubscription);

    const initialDataLoadSubscription = this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(
        take(1),
        concatMap(() => this.dataLoaderService.load()),
      )
      .subscribe();
    this.subscriptions.add(initialDataLoadSubscription);

    const locationChangeSubscription = this.authRestaurantService.select()
      .pipe(
        pairwise(),
        filter(([prev, current]) =>
          !!prev?.location?._id
          && !!current?.location?._id
          && prev.location._id !== current.location._id
        ),
        tap(() => {
          this.socketService.disconnect();
          this.dataLoaderService.reset();
          this.socketService.connect();
        }),
        concatMap(() => this.dataLoaderService.load()),
      )
      .subscribe();
    this.subscriptions.add(locationChangeSubscription);

    const logoutOnDisabledSubscription = this.authRestaurantService.select()
      .pipe(
        filter(user => !user.enabled),
        take(1),
        tap(() => {
          console.log('logoutOnDisabledSubscription logout');
          this.authService.logout();
          this.alertsService.addError('Your account has been disabled by the service administrator. Please contact us if you  have any questions.', '');
        }),
      )
      .subscribe();
    this.subscriptions.add(logoutOnDisabledSubscription);

    this.subscriptions.add(this.permissionChecker.listenChanges().subscribe());
    this.subscriptions.add(this.permissionChecker.isProfileFilled().subscribe());
    this.subscriptions.add(this.audioService.init().subscribe());
    this.subscriptions.add(this.waitTimeService.init().subscribe());

    const authUserRemovedSubscription = this.authRestaurantService.select()
      .pipe(
        filter(value => !Boolean(value._id)),
        tap(() => {
          console.log('authUserRemovedSubscription logout');
          this.authService.logout();
        }),
      )
      .subscribe();
    this.subscriptions.add(authUserRemovedSubscription);

    const offsetTimeSubscription = this.serverTimeService.offset$
      .pipe(
        tap(offset => {
          // @ts-ignore
          Date.setOffset(offset);
        }),
      )
      .subscribe();
    this.subscriptions.add(offsetTimeSubscription);

    const notificationCenterPlanSub = this.authRestaurantService.selectWithAccount()
      .pipe(
        distinctUntilChanged((prev, curr) => {
          const prevEnabled = prev?.account && prev?.location
            ? !isOnFreePlan(prev) && !!prev.account.features?.notificationCenter && !!prev.location.messages?.notificationCenter
            : false;
          const currEnabled = curr?.account && curr?.location
            ? !isOnFreePlan(curr) && !!curr.account.features?.notificationCenter && !!curr.location.messages?.notificationCenter
            : false;
          return prevEnabled === currEnabled;
        }),
        tap((user) => {
          if (!user?.account || !user?.location) {
            return;
          }

          const enabled = !isOnFreePlan(user) && !!user.account.features?.notificationCenter && !!user.location.messages?.notificationCenter;

          if (enabled) {
            this.notificationCenterService.init();
          } else {
            this.notificationCenterService.destroy();
          }
        })
      )
      .subscribe();
    this.subscriptions.add(notificationCenterPlanSub);
  }

  refetchData(): void {
    this.authRestaurantService.load()
      .pipe(take(1))
      .subscribe();

    this.dataLoaderService.load()
      .pipe(take(1))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.dataLoaderService.reset();
    this.socketService.disconnect();
    this.authRestaurantService.reset();
    this.notificationCenterService.destroy();
    this.locationsSwitcherBadgeService.destroy();
  }
}
